import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  MapPin,
  CalendarClock,
  Mail,
  ShieldCheck,
  ChevronDown,
  Menu,
  X,
} from "lucide-react";
import logo from "../assets/logo";
import { NAV_LINKS, SERVICES } from "../data/mock";
import { useQuote } from "./QuoteModal";

const HEADER_CONTACT = {
  address: "8128 128 St #302, Surrey, BC V3W 1R1",
  email: "info@abhifinancial.com",
  hours: "Open 7 days a week, 9:00 to 17:00",
};

function DesktopServicesDropdown({ link }) {
  const [open, setOpen] = useState(false);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const suppressFocusOpen = useRef(false);

  const location = useLocation();
  const dropdownId = `desktop-services-${link.to.replace(
    /[^a-zA-Z0-9]/g,
    "-"
  )}`;

  // Close for navigation, including browser back/forward.
  useEffect(() => {
    setOpen(false);
  }, [location.key, location.pathname]);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return;

    const handleOutsidePointer = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsidePointer);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handleOutsidePointer
      );
    };
  }, [open]);

  const closeAndRestoreFocus = () => {
    setOpen(false);

    // Return focus without reopening the dropdown.
    suppressFocusOpen.current = true;
    triggerRef.current?.focus({ preventScroll: true });
    suppressFocusOpen.current = false;
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeAndRestoreFocus();
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <NavLink
        ref={triggerRef}
        to={link.to}
        data-testid="nav-services"
        aria-expanded={open}
        aria-controls={dropdownId}
        onFocus={() => {
          if (!suppressFocusOpen.current) {
            setOpen(true);
          }
        }}
        onClick={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
       className={({ isActive }) =>
  `nav-link flex items-center gap-1 ${
    isActive || location.pathname.startsWith("/services")
      ? "text-[#006FB9] after:!scale-x-100"
      : ""
  }`
}
      >
        {link.label}

        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </NavLink>

      <div
        id={dropdownId}
        aria-hidden={!open}
        className={`absolute left-0 top-full z-50 w-72 rounded-2xl border border-[#E1ECF3] bg-white p-2 shadow-[0_20px_50px_rgba(0,111,185,0.15)] transition-all duration-300 ${
          open
            ? "visible translate-y-0 opacity-100"
            : "pointer-events-none invisible translate-y-2 opacity-0"
        }`}
      >
        {SERVICES.map((service) => (
          <Link
            key={service.slug}
            to={`/services/${service.slug}`}
            tabIndex={open ? 0 : -1}
            onClick={closeAndRestoreFocus}
            className="block rounded-xl px-4 py-2.5 text-sm font-medium text-[#294A65] transition-colors duration-200 hover:bg-[#EEF6FB] hover:text-[#006FB9]"
          >
            {service.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const { openQuote } = useQuote();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, [location.key, location.pathname]);

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  };

  return (
    <>
      {/* Top bar */}
      <div className="hidden bg-[#006FB9] text-[#D1E7F3] lg:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-2 text-[13px]">
          <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-2">
           <a
  href="https://www.google.com/maps/dir/?api=1&destination=8128%20128%20St%20%23302%2C%20Surrey%2C%20BC%20V3W%201R1%2C%20Canada"
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Open Abhi Insurance Agency location in Google Maps"
  className="flex items-center gap-1.5 transition-colors hover:text-white"
>
  <MapPin
    aria-hidden="true"
    className="h-3.5 w-3.5 shrink-0"
  />

  <span>{HEADER_CONTACT.address}</span>
</a>

            <a
              href={`mailto:${HEADER_CONTACT.email}`}
              className="flex items-center gap-1.5 whitespace-nowrap transition-colors hover:text-white"
            >
              <Mail
                aria-hidden="true"
                className="h-3.5 w-3.5 shrink-0"
              />
              {HEADER_CONTACT.email}
            </a>

            <button
              type="button"
              onClick={() => openQuote()}
              className="flex items-center gap-1.5 whitespace-nowrap transition-colors hover:text-white"
            >
              <CalendarClock
                aria-hidden="true"
                className="h-3.5 w-3.5 shrink-0"
              />
              Schedule a Call
            </button>
          </div>

          <span className="ml-auto shrink-0 whitespace-nowrap text-right">
            {HEADER_CONTACT.hours}
          </span>
        </div>
      </div>

      {/* Main navigation */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 shadow-[0_4px_24px_rgba(0,111,185,0.08)] backdrop-blur-md"
            : "bg-white"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5">
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="flex items-center"
            data-testid="logo-link"
          >
            <img
              src={logo}
              alt="Abhi Insurance Agency"
              className="h-12 w-auto sm:h-14"
            />
          </Link>

          <nav
            aria-label="Main navigation"
            className="hidden items-center gap-1 lg:flex"
          >
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <DesktopServicesDropdown
                  key={link.to}
                  link={link}
                />
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`nav-${link.label
                    .toLowerCase()
                    .replace(/\s/g, "-")}`}
                 className={({ isActive }) =>
  `nav-link ${
    isActive
      ? "text-[#006FB9] after:!scale-x-100"
      : ""
  }`
}
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
           <button
  type="button"
  onClick={() => openQuote()}
  className="btn-primary !hidden lg:!inline-flex"
  data-testid="header-get-quote"
>
  Get Quote
  <ShieldCheck className="h-4 w-4" />
</button>

            <button
              type="button"
              onClick={() => {
                setMobileOpen((previous) => !previous);
                setMobileServicesOpen(false);
              }}
              className="grid h-10 w-10 place-items-center rounded-xl border border-[#DCEAF2] text-[#006FB9] lg:hidden"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="header-mobile-menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          id="header-mobile-menu"
          className={`overflow-hidden bg-white transition-all duration-400 lg:hidden ${
            mobileOpen
              ? "visible max-h-[560px] border-t border-[#E1ECF3]"
              : "invisible max-h-0"
          }`}
        >
          <nav
            aria-label="Mobile navigation"
            className="space-y-1 px-6 py-4"
          >
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <div key={link.to}>
                  <div className="flex w-full items-stretch rounded-xl">
                    <NavLink
                      to={link.to}
                      onClick={closeMobileMenu}
                      className="flex flex-1 items-center rounded-l-xl px-3 py-2.5 text-[15px] font-semibold text-[#193B58]"
                    >
                      {link.label}
                    </NavLink>

                    <button
                      type="button"
                      onClick={() =>
                        setMobileServicesOpen((previous) => !previous)
                      }
                      aria-label={
                        mobileServicesOpen
                          ? "Hide service pages"
                          : "Show service pages"
                      }
                      aria-expanded={mobileServicesOpen}
                      aria-controls="mobile-services-menu"
                      className="flex w-12 shrink-0 items-center justify-end rounded-r-xl px-3 py-2.5 text-[#193B58]"
                    >
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          mobileServicesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>

                  <div
                    id="mobile-services-menu"
                    className={`overflow-hidden transition-all duration-300 ${
                      mobileOpen && mobileServicesOpen
                        ? "visible max-h-72"
                        : "invisible max-h-0"
                    }`}
                  >
                    {SERVICES.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        onClick={closeMobileMenu}
                        className="block rounded-lg px-6 py-2 text-sm text-[#45647C]"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  onClick={closeMobileMenu}
                  className="block rounded-xl px-3 py-2.5 text-[15px] font-semibold text-[#193B58]"
                >
                  {link.label}
                </NavLink>
              )
            )}

            <button
              type="button"
              onClick={() => {
                closeMobileMenu();
                openQuote();
              }}
              className="btn-primary mt-2 w-full justify-center"
              data-testid="mobile-get-quote"
            >
              Get Quote
              <ShieldCheck className="h-4 w-4" />
            </button>
          </nav>
        </div>
      </header>
    </>
  );
}
